import { useState, useRef, useMemo } from 'react';

import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

import Slider from 'react-slick';

import { Section } from '../../styles';
import {
  TestimonialsContentContainer,
  TestimonialsHeading,
  TestimonialWrapper,
  TestimonialSliderColumn,
  SliderNavigationButtonLeft,
  SliderNavigationButtonRight,
  PaginationDots,
  PaginationDotsViewport,
  PaginationDotsTrack,
  Dot,
  PAGINATION_DOT_SIZE_PX,
  PAGINATION_DOT_GAP_PX,
  PAGINATION_DOT_STEP_PX,
} from './styles';
import shuffle from 'lodash/shuffle';

import { testimonialsslider } from '../../constants';
import TestimonyCard from './components/TestimonyCard';

const MAX_PAGINATION_DOTS = 10;

const TestimonialsSection = () => {
  const [activeSlide, setActiveSlide] = useState(0);
  const sliderRef = useRef(null);

  const shuffledTestimonials = useMemo(
    () => shuffle(testimonialsslider),
    []
  );

  const slideCount = shuffledTestimonials.length;

  const paginationWindowStart = useMemo(() => {
    if (slideCount <= MAX_PAGINATION_DOTS) return 0;
    const half = Math.floor(MAX_PAGINATION_DOTS / 2);
    let start = activeSlide - half;
    return Math.max(0, Math.min(start, slideCount - MAX_PAGINATION_DOTS));
  }, [slideCount, activeSlide]);

  const paginationViewportWidthPx = useMemo(() => {
    if (slideCount === 0) return 0;
    const n = Math.min(slideCount, MAX_PAGINATION_DOTS);
    return (
      n * PAGINATION_DOT_SIZE_PX + Math.max(0, n - 1) * PAGINATION_DOT_GAP_PX
    );
  }, [slideCount]);

  const handleDotClick = (slideIndex) => {
    if (sliderRef.current) {
      sliderRef.current.slickGoTo(slideIndex);
    }
  };

  const settings = {
    infinite: true,
    lazyLoad: true,
    speed: 500,
    slidesToShow: 3,
    centerMode: true,
    centerPadding: '0px',
    autoplaySpeed: 5000,
    slidesToScroll: 1,
    autoplay: true,
    dots: false,
    accessibility: true,
    focusOnSelect: false,
    swipe: true,
    beforeChange: (current, next) => {
      setActiveSlide(next);
    },
    responsive: [
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1,
          centerMode: false,
        },
      },
    ],
  };

  const handlePrevClick = () => {
    if (sliderRef.current) {
      sliderRef.current.slickPrev();
    }
  };

  const handleNextClick = () => {
    if (sliderRef.current) {
      sliderRef.current.slickNext();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Tab' && sliderRef.current) {
      e.preventDefault();
      e.stopPropagation();

      if (e.shiftKey) {
        sliderRef.current.slickPrev();
      } else {
        sliderRef.current.slickNext();
      }
    }
  };

  return (
    <Section>
      <TestimonialsContentContainer>
        <TestimonialsHeading>Stories of support and change</TestimonialsHeading>
        <SliderNavigationButtonLeft onClick={handlePrevClick}>
          <ArrowBackIosNewIcon sx={{ color: '#2c2c2c' }} />
        </SliderNavigationButtonLeft>
        <TestimonialWrapper
          onKeyDown={handleKeyDown}
          tabIndex={0}
          role="region"
          aria-label="Testimonials slider"
        >
          <TestimonialSliderColumn>
            <Slider ref={sliderRef} {...settings}>
              {shuffledTestimonials.map((item, index) => (
                <div key={item.id}>
                  <TestimonyCard item={item} isActive={index === activeSlide} />
                </div>
              ))}
            </Slider>
            {slideCount > 0 && (
              <PaginationDots>
                <PaginationDotsViewport viewportWidthPx={paginationViewportWidthPx}>
                  <PaginationDotsTrack
                    translateXPx={paginationWindowStart * PAGINATION_DOT_STEP_PX}
                    role="tablist"
                    aria-label="Testimonial slides"
                  >
                    {Array.from({ length: slideCount }, (_, slideIndex) => (
                      <Dot
                        key={slideIndex}
                        component="button"
                        type="button"
                        active={activeSlide === slideIndex}
                        onClick={() => handleDotClick(slideIndex)}
                        aria-label={`Go to testimonial ${slideIndex + 1} of ${slideCount}`}
                        aria-current={activeSlide === slideIndex ? 'true' : undefined}
                      />
                    ))}
                  </PaginationDotsTrack>
                </PaginationDotsViewport>
              </PaginationDots>
            )}
          </TestimonialSliderColumn>
        </TestimonialWrapper>
        <SliderNavigationButtonRight onClick={handleNextClick}>
          <ArrowForwardIosIcon sx={{ color: '#2c2c2c' }} />
        </SliderNavigationButtonRight>
      </TestimonialsContentContainer>
    </Section>
  );
};

export default TestimonialsSection;
